<script setup lang="ts">
import { ref } from 'vue';
import { Config } from '@/config';
import { useRoute } from 'vue-router';
import { constant } from '@/constant';
import { getLabel, getTranslation } from '@/utils/data-display-utils';

import mobileMenu from '@/components/mobile/mobileMenu.vue';
import LoginDialog from '@/components/dialogs/LoginDialog.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import AccountSection from '@/components/AccountSection.vue';
import LocaleSelect from '@/components/LocaleSelect.vue';
import RoundLogoImage from '@/components/RoundLogoImage.vue';

import { useUserStore } from '@/store/userStore';
import { useCommunityStore } from '@/store/communityStore';
import { paths } from '@/utils/paths';
import { authService } from '@/services/auth.service';
import useResponsive from '@/utils/responsive-util';
import { useTracker } from '@/composables/useTracker';
import { Locales } from '@/store/language.interface';
import { useCurrentLocale } from '@/plugins/i18n';
import { useLocaleChoice } from '@/composables/useLocaleChoice';

// PROPS
const props = defineProps<{
  languageEnabled?: Locales[];
}>();

// STORE
const userStore = useUserStore();
const communityStore = useCommunityStore();

// REFS
const loginDialogRef = ref<InstanceType<typeof BaseDialog>>();

// CONST
const { isMobile } = useResponsive();
const results = ref(false);
const route = useRoute();
const { track } = useTracker();
const currentLocale = useCurrentLocale();
const { chooseLocale } = useLocaleChoice();
const communityTitle = getTranslation(communityStore.community, 'title', props.languageEnabled) ?? '';

// EMITS
const emit = defineEmits<{
  (e: 'results', value): void;
}>();

// FUNCTIONS
function onLogin() {
  track({
    event: 'login_click',
    params: {
      location: 'header',
    },
  });
  authService.login();
}

const openAdmin = () => {
  window.open(`${Config.admin.url}`, '_blank');
};

function openResults() {
  results.value = !results.value;
  emit('results', results.value);
}

function shouldDisplayAdmin() {
  return userStore.isAdmin() || userStore.isEditor();
}

function shouldDisplayResults() {
  if (!route.path.includes('consultation')) {
    return false;
  } else if (userStore.isAdmin()) {
    return true;
  }
  const slug = route.params.slug as string;
  const consultation = communityStore.community.consultations.find((x) => x.slug === slug);
  return userStore.isEditor(constant.HomePageItem.Type.Consultation, consultation?.id);
}
</script>

<template>
  <header class="header">
    <div class="header__items">
      <router-link
        class="header__logo"
        :to="paths.root"
        :aria-label="
          getLabel('aria-label.header.go-to-home', props.languageEnabled, {
            named: { client: communityTitle },
          })
        "
      >
        <RoundLogoImage
          :source="communityStore.community.logoFile.url"
          :color="communityStore.community.logoFile.color"
          alt-text=""
        />
      </router-link>

      <template v-if="isMobile">
        <div class="header__mobile">
          <AccountSection v-if="userStore.isUserLogged" :language-enabled="languageEnabled" />
          <mobile-menu @open-login-dialog="onLogin()" :language-enabled="languageEnabled" />
        </div>
      </template>

      <template v-else>
        <nav class="header__navigation" :aria-label="getLabel('aria-label.header.navigation', props.languageEnabled)">
          <button
            v-if="shouldDisplayResults()"
            class="header__navigation--action q-mr-md"
            :aria-pressed="results"
            @click="openResults"
          >
            {{ getLabel('consultation.results', props.languageEnabled) }}
          </button>
          <button v-if="shouldDisplayAdmin()" class="header__navigation--action q-mr-md" @click="openAdmin">
            admin
          </button>
          <router-link class="header__navigation--item" :to="paths.about">
            {{ getLabel('about-us', props.languageEnabled) }}
          </router-link>
          <q-separator vertical aria-hidden="true" />
          <div class="header__navigation--locale">
            <LocaleSelect
              v-if="languageEnabled?.length > 1"
              :model-value="currentLocale"
              :language-enabled="languageEnabled"
              @update:model-value="chooseLocale"
            />
          </div>
          <q-separator v-if="languageEnabled?.length > 1" vertical aria-hidden="true" />
          <AccountSection v-if="userStore.isUserLogged" :language-enabled="languageEnabled" />
          <button v-else class="header__navigation--item" @click="onLogin()">
            {{ getLabel('account.login', props.languageEnabled) }}
          </button>
        </nav>
      </template>
    </div>
  </header>
  <LoginDialog ref="loginDialogRef" :language-enabled="languageEnabled" />
</template>

<style lang="scss">
.header {
  z-index: 2;
  position: -webkit-sticky;
  position: sticky !important;
  top: 0;
  align-items: center;
  background-color: $color-neutral-white;
  box-shadow: 0 2px 10px rgba(1, 0, 0, 0.1);
  height: $header-height;

  &__items {
    height: inherit;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 32px;
  }

  &__logo {
    width: 60px;
    height: 60px;

    &:hover {
      border-bottom: 1px solid transparent;
    }

    &:focus-visible {
      outline: 2px solid $color-primary;
      outline-offset: 3px;
      border-radius: 50%;
    }
  }

  &__navigation {
    display: flex;
    align-items: center;

    &--item {
      text-decoration: none;
      border-bottom: 1px solid transparent;
      color: $color-neutral-black;
      transition: all 200ms ease;
      cursor: pointer;

      &:hover {
        color: $color-primary;
        border-bottom: 1px solid transparent;
        transition: all 200ms ease;
      }

      &:focus-visible {
        outline: 2px solid $color-primary;
        outline-offset: 2px;
        border-radius: 2px;
      }
    }

    &--action {
      appearance: none;
      border: none;
      border-radius: 4px;
      padding: 4px 12px;
      font-size: inherit;
      font-family: inherit;
      cursor: pointer;
      transition: all 200ms ease;
      background-color: $color-primary;
      color: $color-neutral-white;

      &:hover {
        opacity: 0.85;
      }

      &:focus-visible {
        outline: 2px solid $color-primary;
        outline-offset: 2px;
      }

      &[aria-pressed='true'] {
        background-color: $color-primary;
      }
    }

    &--icon {
      margin-left: 24px;
      height: 24px !important;
      width: 24px !important;
    }
  }

  .q-separator {
    margin: $space-xs $space-md;
    height: 32px;
  }

  &__menu {
    .q-item__section {
      margin: 0 16px;
    }
  }
}

@media screen and (max-width: $breakpoint-xl) {
  .header {
    &__mobile {
      display: flex;
    }

    &__items {
      margin: 0 20px;
    }
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .header {
    height: 56px;

    &__items {
      margin: 0 10px;
    }

    &__logo {
      width: 40px;
      height: 40px;
    }
  }
}
</style>
